<template>
  <a-row>
    <div>
      <!-- Defaced threshold -->
      <div style="margin-top: 8px">
        <div class="setting-title">
          <span>
            <b>Defaced confidence threshold:</b>
          </span>
          <a-popover title="Note for threshold:">
            <template slot="content">
              <div style="width: 300px">
                The webpage is considered as defaced if the confidence given by the model is greater than the threshold. <br>
                A threshold that is too high means we may miss true defacements. A threshold that is too low means we may get more false alerts. <br>
                <b>The default threshold is 50%</b> and the values too low or too high are not recommended.
              </div>
            </template>
            <a-icon class="explain" type="info-circle" theme="filled" />
          </a-popover>
        </div>
        <div class="setting-body" style="display: flex; flex-direction: row; justify-content: flex-start">
          <a-slider
            v-model="display_defaced_threshold"
            :min="1"
            :max="99"
            :step="1"
            :marks="marks"
            style="min-width: 300px; width: 75%;"
          />
          <div style="margin-left: 20px;">
            <div style="display: flex;">
              <div>
                <span>Current threshold:</span>
              </div>
              <a-input-number
                v-model="display_defaced_threshold"
                :min="1"
                :max="99"
                :step="1"
                style="margin-left: 30px;"
                :formatter="(value) => `${value}%`"
              />
            </div>
          </div>
        </div>
      </div>

      <a-divider />
      <!-- Words ignored -->
      <div>
        <div class="setting-title">
          <span>
            <b>Words to be ignored:</b>
          </span>
          <a-popover title="Note for words to be ignored:">
            <template slot="content">
              <div style="width: 300px">
                These words in the HTML content will be excluded from the Model to prevent false alerts.
              </div>
            </template>
            <a-icon class="explain" type="info-circle" theme="filled" />
          </a-popover>
        </div>
        <div class="setting-body" style="margin-top: 12px">
          <template v-for="word in words_ignored">
            <a-tooltip v-if="word.length > 27" :key="word" :title="word">
              <a-tag
                :key="word"
                color="red"
                :closable="true"
                @close="() => remove_word_to_ignore(word)"
              >
                {{ `${word.slice(0, 27)}...` }}
              </a-tag>
            </a-tooltip>
            <a-tag
              v-else
              :key="word"
              color="red"
              :closable="true"
              @close="() => remove_word_to_ignore(word)"
            >
              {{ word }}
            </a-tag>
          </template>
          <span v-if="add_word_ignored_visible">
            <a-input
              ref="input"
              type="text"
              size="small"
              :style="{ width: '78px' }"
              :value="input_word_to_ignore"
              @change="input_word_to_ignore_change"
              @keyup.enter="handle_add_word_to_ignore_confirm"
            />
            <a-button size="large" style="margin-left: 5px" @click="handle_add_word_to_ignore_confirm">
              Add
            </a-button>
          </span>
          <a-tag
            v-else
            style="background: #fff; borderstyle: dashed"
            @click="show_add_word_ignored_visible"
          >
            <a-icon type="plus" /> Add word to ignore
          </a-tag>
        </div>
        <a-button type="primary" style="margin-top: 20px; margin-left: 10px" @click="change_show_advanced_configuration">
          Show Model Explainability Tool
        </a-button>
      </div>
      <!-- <a-divider /> -->
      <!-- Explainability -->
      <div>
        <!-- <div class="setting-title">
          <span>
            <b>Model Explainability Tool:</b>
          </span>
          <a-popover title="Note for word to ignore:">
            <template slot="content">
              <div style="width: 300px">
                If you are facing false alert, you can try the explainability to find out the cause.
              </div>
            </template>
            <a-icon class="explain" type="info-circle" theme="filled" />
          </a-popover>
        </div> -->
        <div class="setting-body" style="margin-top: 12px">
          <!-- <a-button type="primary" @click="change_show_advanced_configuration">
            Show Model Explainability Tool
          </a-button> -->
          <a-modal
            :bodyStyle="{ 'height': '80vh',
                          'overflow': 'scroll',
                          'overflow-x':'hidden'}"
            style="overflow: scroll;"
            :visible="show_advanced_configuration" 
            width="75%"
            :closable="true"
            
            @ok="handle_Ok_and_save"
            @cancel="change_show_advanced_configuration"
          >
            <template slot="title">
              <a-tooltip>
                <template slot="title">
                  The model explainability tool runs on the baseline webpage only to analyze the cause of the alerts. If the analyzed text and ignored words are not changed, the results shown will also not be changed.
                </template>
                Model Explainability Result <a-icon class="explain" type="info-circle" theme="filled" />
              </a-tooltip>
            </template>
            <NLPAnalysisResult :modalClosed="!show_advanced_configuration" :model="{model: model_name, threshold: defaced_threshold, words_ignored: words_ignored}" @save-config="save_config" @change="updateFromAnalysis" />
          </a-modal>
        </div>
      </div>
    </div>
  </a-row>
</template>

<script>
import NLPAnalysisResult from "./NLPAnalysisResult";

export default {
  name: "NLPModel",
  components: {
    NLPAnalysisResult
  },
  props: {
    model: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      show_advanced_configuration: false,
      model_name: this.model.model_name,
      enabled: !!this.model.enabled,
      defaced_threshold: this.model.defaced_threshold,
      words_ignored: this.model.words_ignored,
      input_word_to_ignore: "",
      add_word_ignored_visible: false,
      marks: {
        1: {
          style: {
            color: "gray",
          },
          label: <span>1%</span>,
        },
        50: {
          label: (
            <span> <b>50%</b> &nbsp;
            <a-tooltip placement="right">
              <template slot="title">Recommended alert threshold value</template>
              <a-icon type="like" style="color: green;"/>
            </a-tooltip>
            </span>
          ),
        },
        99: {
          style: {
            color: "gray",
          },
          label: <span>99%</span>,
        },
      },
    };
  },
  computed: {
    display_defaced_threshold: {
      get: function () {
        return Math.round(this.defaced_threshold * 100);
      },
      set: function (value) {
        this.defaced_threshold = value / 100;
        this.updateParent();
      },
    },
  },
  watch: {
    model: {
      handler() {
        this.assignValueToEngine();
      },
      deep: true,
    },
    enabled: {
      handler() {
        this.updateParent();
      },
      deep: true,
    },
  },
  methods: {
    change_show_advanced_configuration(){
      this.show_advanced_configuration = !this.show_advanced_configuration;
    },
    async handle_Ok_and_save(){
      this.show_advanced_configuration = false;
      await this.updateParent();
      await this.save_config();
    },
    updateFromAnalysis(config){
      this.words_ignored = config.words_ignored;
      this.updateParent();
    },
    updateParent() {
      this.$emit("change", {
        model_name: this.model_name,
        defaced_threshold: this.defaced_threshold,
        enabled: this.enabled,
        words_ignored: this.words_ignored,
      });
    },
    assignValueToEngine() {
      this.model_name = this.model.model_name;
      this.enabled = !!this.model.enabled;
      this.defaced_threshold = this.model.defaced_threshold;
      this.words_ignored = this.model.words_ignored === "[]" ? [] : this.model.words_ignored;
    },
    // for adding word to ignore
    show_add_word_ignored_visible() {
      this.add_word_ignored_visible = true;
      this.input_word_to_ignore = "";
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },
    input_word_to_ignore_change(e) {
      this.input_word_to_ignore = e.target.value;
    },
    handle_add_word_to_ignore_confirm() {
      let words = this.words_ignored;
      if (!this.input_word_to_ignore) {
        this.$message.error("Please enter the word to ignore!");
        return;
      }
      if (words.includes(this.input_word_to_ignore)) {
        this.$message.error(`Word "${this.input_word_to_ignore}" already in ignore list!`);
        return;
      }else {
        words = [...words, this.input_word_to_ignore];
        this.words_ignored = words;
        this.$message.success(`Word "${this.input_word_to_ignore}" added to ignore list!`);
      }
      this.add_word_ignored_visible = false;
      this.input_word_to_ignore = "";
      this.updateParent();
    },
    add_word_to_ignore(word) {
      let words = this.words_ignored;
      if (words.indexOf(word) === -1) {
        words = [...words, word];
        this.words_ignored = words;
        this.$message.success(`Word "${word}" added to ignore list!`);
        this.updateParent();
      } else {
        this.$message.error(`Word "${word}" already in ignore list!`);
      }
    },
    remove_word_to_ignore(word) {
      const words = this.words_ignored.filter((w) => w !== word);
      this.words_ignored = words;
      this.$message.info(`Word "${word}" removed from ignore list!`);
      this.updateParent();
    },
    async save_config(){
      this.isLoading = true;
      await this.$emit("save-config")
      this.isLoading = false;
    }
  },
};
</script>
<style scope>
.setting-title{
  font-size: 16px;
  font-weight: 600;
}
.setting-body{
  margin-left: 10px;
}
</style>
<style>
.ant-slider-with-marks{
  margin-bottom: 18px;
}
.ant-divider-horizontal{
  margin: 24px 0 20px 0;
  height: 2px;
}
.ant-card-head{
  border-bottom: 1px solid #e8e8e8 !important;
}
.ant-card-bordered{
  border: 2px solid #e8e8e8 !important;
}
.ant-card-body{
  padding: 12px;
}
.ant-input-number{
  width: 64px;
}
</style>
